// ════════════════════════════════════════════════════════════════════════════
//  Udisys Gestor Web  —  pages/Financeiros/GerenciamentoCartao.tsx
//  Tela de gerenciamento e conciliação de cartões (3 abas: Consulta,
//  Lançamento Manual, Conciliação) — espelha o frm_gerenciamento_cartao.cs
//  do desktop legado, com integração ao cálculo automático de taxas.
// ════════════════════════════════════════════════════════════════════════════

import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  CreditCard, Search, Plus, CheckCircle2, X, Loader2, RefreshCw, Calculator,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { Checkbox } from '@/components/ui/checkbox';
import { useToast } from '@/hooks/use-toast';
import { getApiErrorMessage } from '@/lib/apiClient';
import {
  cartaoMovimentoService, type MovCartao, type CalculoTaxa,
} from '@/features/financeiro/services/cartaoMovimentoService';
import { cadastrosFinanceirosService } from '@/features/financeiro/services/cadastrosFinanceirosService';

// ── Helpers ──────────────────────────────────────────────────────────────────

const fmtCur = (v: number) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v);

const fmtDate = (s: string | null | undefined) =>
  s ? new Date(s).toLocaleDateString('pt-BR') : '—';

const today = () => new Date().toISOString().slice(0, 10);
const daysAgo = (n: number) => {
  const d = new Date(); d.setDate(d.getDate() - n); return d.toISOString().slice(0, 10);
};
const daysAhead = (n: number) => {
  const d = new Date(); d.setDate(d.getDate() + n); return d.toISOString().slice(0, 10);
};

// ── Component ────────────────────────────────────────────────────────────────

export default function GerenciamentoCartao() {
  const { toast } = useToast();

  // Combos
  const [cartoes, setCartoes] = useState<{ id: number; nome: string }[]>([]);
  const [contas, setContas] = useState<{ id: number; descricao: string }[]>([]);

  useEffect(() => {
    Promise.all([
      cadastrosFinanceirosService.listarCartoes({ apenasAtivos: true, tamanhoPagina: 200 }).then((r) =>
        (r.itens as unknown as { id: number; nome?: string; descricao?: string }[]).map((c) => ({
          id: c.id, nome: c.nome ?? c.descricao ?? `Cartão ${c.id}`,
        })),
      ),
      cadastrosFinanceirosService.getContasCorrentesCombo().then((r) =>
        (r as unknown as { id: number; descricao: string }[]),
      ),
    ])
      .then(([cs, cc]) => { setCartoes(cs); setContas(cc); })
      .catch(() => toast({ title: 'Erro carregando combos', variant: 'destructive' }));
  }, [toast]);

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-3xl font-bold flex items-center gap-3">
          <CreditCard className="h-8 w-8" />
          Gerenciamento de Cartões
        </h1>
        <p className="text-muted-foreground mt-1">
          Acompanhe vendas em cartões, lance movimentos manuais e concilie recebimentos.
        </p>
      </div>

      <Tabs defaultValue="consulta">
        <TabsList>
          <TabsTrigger value="consulta" className="gap-2"><Search className="h-4 w-4" />Consulta</TabsTrigger>
          <TabsTrigger value="lancamento" className="gap-2"><Plus className="h-4 w-4" />Lançamento Manual</TabsTrigger>
          <TabsTrigger value="conciliacao" className="gap-2"><CheckCircle2 className="h-4 w-4" />Conciliação</TabsTrigger>
        </TabsList>

        <TabsContent value="consulta">
          <ConsultaTab cartoes={cartoes} />
        </TabsContent>

        <TabsContent value="lancamento">
          <LancamentoTab cartoes={cartoes} />
        </TabsContent>

        <TabsContent value="conciliacao">
          <ConciliacaoTab cartoes={cartoes} contas={contas} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
//  Aba Consulta
// ════════════════════════════════════════════════════════════════════════════

function ConsultaTab({ cartoes }: { cartoes: { id: number; nome: string }[] }) {
  const { toast } = useToast();
  const [dtInicio, setDtInicio] = useState(daysAgo(30));
  const [dtFim, setDtFim] = useState(daysAhead(30));
  const [tipoData, setTipoData] = useState<'movimento' | 'previsao' | 'recebimento'>('movimento');
  const [cartaoId, setCartaoId] = useState<number | undefined>(undefined);
  const [situacao, setSituacao] = useState<'pendente' | 'conciliado' | 'cancelado' | undefined>(undefined);
  const [movs, setMovs] = useState<MovCartao[]>([]);
  const [carregando, setCarregando] = useState(false);

  const carregar = useCallback(async () => {
    setCarregando(true);
    try {
      const r = await cartaoMovimentoService.listar({
        dtInicio, dtFim, tipoData, cartaoId, situacao,
        pagina: 1, tamanhoPagina: 200,
      });
      setMovs(r.itens);
    } catch (e) {
      toast({ title: 'Erro ao carregar', description: getApiErrorMessage(e), variant: 'destructive' });
    } finally {
      setCarregando(false);
    }
  }, [dtInicio, dtFim, tipoData, cartaoId, situacao, toast]);

  useEffect(() => { carregar(); }, [carregar]);

  const totais = useMemo(() => ({
    bruto:    movs.reduce((s, m) => s + m.valorOperacao, 0),
    taxa:     movs.reduce((s, m) => s + m.valorTaxa + m.valorAdicional + m.valorAntecipacao, 0),
    liquido:  movs.reduce((s, m) => s + m.valorLiquido, 0),
    recebido: movs.reduce((s, m) => s + m.valorRecebido, 0),
  }), [movs]);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Filtros</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <div className="space-y-1">
            <Label className="text-xs">Tipo de Data</Label>
            <Select value={tipoData} onValueChange={(v) => setTipoData(v as 'movimento' | 'previsao' | 'recebimento')}>
              <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="movimento">Data Movimento</SelectItem>
                <SelectItem value="previsao">Data Previsão</SelectItem>
                <SelectItem value="recebimento">Data Recebimento</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">De</Label>
            <Input type="date" value={dtInicio} onChange={(e) => setDtInicio(e.target.value)} className="h-9" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Até</Label>
            <Input type="date" value={dtFim} onChange={(e) => setDtFim(e.target.value)} className="h-9" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Cartão</Label>
            <Select value={cartaoId?.toString() ?? 'todos'}
              onValueChange={(v) => setCartaoId(v === 'todos' ? undefined : Number(v))}>
              <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="todos">Todos</SelectItem>
                {cartoes.map((c) => <SelectItem key={c.id} value={c.id.toString()}>{c.nome}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Situação</Label>
            <Select value={situacao ?? 'todas'}
              onValueChange={(v) => setSituacao(v === 'todas' ? undefined : v as 'pendente' | 'conciliado' | 'cancelado')}>
              <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="todas">Todas</SelectItem>
                <SelectItem value="pendente">Pendente</SelectItem>
                <SelectItem value="conciliado">Conciliado</SelectItem>
                <SelectItem value="cancelado">Cancelado</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* KPIs */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 pt-2 border-t">
          <KpiCard label="Bruto"     value={totais.bruto} />
          <KpiCard label="Taxas"     value={totais.taxa} color="text-red-600" />
          <KpiCard label="Líquido"   value={totais.liquido} color="text-blue-600" />
          <KpiCard label="Recebido"  value={totais.recebido} color="text-green-600" />
        </div>

        {/* Tabela */}
        <div className="border rounded-lg overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Lanç.</TableHead>
                <TableHead>Cartão</TableHead>
                <TableHead>Dt Mov.</TableHead>
                <TableHead>Dt Prev.</TableHead>
                <TableHead>Dt Rec.</TableHead>
                <TableHead className="text-right">Bruto</TableHead>
                <TableHead className="text-right">Taxa</TableHead>
                <TableHead className="text-right">Líquido</TableHead>
                <TableHead className="text-right">Parcela</TableHead>
                <TableHead className="text-center">Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {carregando ? (
                <TableRow><TableCell colSpan={10} className="text-center py-6">
                  <Loader2 className="h-4 w-4 animate-spin inline-block mr-2" />Carregando...
                </TableCell></TableRow>
              ) : movs.length === 0 ? (
                <TableRow><TableCell colSpan={10} className="text-center text-muted-foreground py-8">
                  Nenhum movimento no período.
                </TableCell></TableRow>
              ) : (
                movs.map((m) => (
                  <TableRow key={m.id}>
                    <TableCell className="font-mono text-xs">{m.id}</TableCell>
                    <TableCell className="font-medium">{m.nomeCartao}</TableCell>
                    <TableCell>{fmtDate(m.dtLancamento)}</TableCell>
                    <TableCell>{fmtDate(m.dtPrevisaoCartao)}</TableCell>
                    <TableCell>{fmtDate(m.dtRecebimento)}</TableCell>
                    <TableCell className="text-right tabular-nums">{fmtCur(m.valorOperacao)}</TableCell>
                    <TableCell className="text-right tabular-nums text-red-600">
                      {fmtCur(m.valorTaxa + m.valorAdicional + m.valorAntecipacao)}
                    </TableCell>
                    <TableCell className="text-right tabular-nums font-semibold">{fmtCur(m.valorLiquido)}</TableCell>
                    <TableCell className="text-right">{m.parcela}/{m.qtdParcelas}</TableCell>
                    <TableCell className="text-center">
                      {m.isCancelado ? <Badge variant="destructive">Cancelado</Badge>
                        : m.isConciliado ? <Badge className="bg-green-600">Conciliado</Badge>
                        : <Badge variant="secondary">Pendente</Badge>}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>

        <Button variant="ghost" size="sm" onClick={carregar} disabled={carregando}>
          <RefreshCw className={`h-3 w-3 mr-1 ${carregando ? 'animate-spin' : ''}`} />Atualizar
        </Button>
      </CardContent>
    </Card>
  );
}

// ════════════════════════════════════════════════════════════════════════════
//  Aba Lançamento Manual
// ════════════════════════════════════════════════════════════════════════════

function LancamentoTab({ cartoes }: { cartoes: { id: number; nome: string }[] }) {
  const { toast } = useToast();
  const [cartaoId, setCartaoId] = useState<number | undefined>(undefined);
  const [dtLancamento, setDtLancamento] = useState(today());
  const [valorOperacao, setValorOperacao] = useState('0');
  const [parcelas, setParcelas] = useState(1);
  const [nsu, setNsu] = useState('');
  const [autenticacao, setAutenticacao] = useState('');
  const [aplicarAntecipacao, setAplicarAntecipacao] = useState(false);
  const [observacao, setObservacao] = useState('');

  const [calculo, setCalculo] = useState<CalculoTaxa | null>(null);
  const [calculando, setCalculando] = useState(false);
  const [salvando, setSalvando] = useState(false);

  // Recalcula automaticamente quando cartão/valor/parcelas mudam
  useEffect(() => {
    if (!cartaoId || Number(valorOperacao) <= 0) {
      setCalculo(null);
      return;
    }

    let active = true;
    setCalculando(true);
    cartaoMovimentoService.calcularTaxa(cartaoId, Number(valorOperacao), parcelas, aplicarAntecipacao)
      .then((c) => { if (active) setCalculo(c); })
      .catch(() => { if (active) setCalculo(null); })
      .finally(() => { if (active) setCalculando(false); });

    return () => { active = false; };
  }, [cartaoId, valorOperacao, parcelas, aplicarAntecipacao]);

  const handleSalvar = useCallback(async () => {
    if (!cartaoId) { toast({ title: 'Selecione o cartão', variant: 'destructive' }); return; }
    if (Number(valorOperacao) <= 0) { toast({ title: 'Valor deve ser maior que zero', variant: 'destructive' }); return; }

    setSalvando(true);
    try {
      const { id } = await cartaoMovimentoService.lancarManual({
        cartaoId,
        dtLancamento,
        valorOperacao: Number(valorOperacao),
        parcelas,
        nsu: nsu || undefined,
        numeroAutenticacaoTef: autenticacao ? Number(autenticacao) : undefined,
        aplicarAntecipacao,
        observacao: observacao || undefined,
      });
      toast({ title: `Movimento #${id} lançado com sucesso` });
      // Reset
      setValorOperacao('0');
      setParcelas(1);
      setNsu('');
      setAutenticacao('');
      setObservacao('');
      setCalculo(null);
    } catch (e) {
      toast({ title: 'Erro ao lançar', description: getApiErrorMessage(e), variant: 'destructive' });
    } finally {
      setSalvando(false);
    }
  }, [cartaoId, dtLancamento, valorOperacao, parcelas, nsu, autenticacao, aplicarAntecipacao, observacao, toast]);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2">
          <Calculator className="h-4 w-4" />
          Lançar Movimento Manual
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Cartão</Label>
            <Select value={cartaoId?.toString() ?? ''} onValueChange={(v) => setCartaoId(Number(v))}>
              <SelectTrigger><SelectValue placeholder="Selecione o cartão" /></SelectTrigger>
              <SelectContent>
                {cartoes.map((c) => <SelectItem key={c.id} value={c.id.toString()}>{c.nome}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Data do Movimento</Label>
            <Input type="date" value={dtLancamento} onChange={(e) => setDtLancamento(e.target.value)} />
          </div>
          <div className="space-y-2">
            <Label>Valor da Operação (R$)</Label>
            <Input type="number" step="0.01" min="0" value={valorOperacao}
              onChange={(e) => setValorOperacao(e.target.value)} />
          </div>
          <div className="space-y-2">
            <Label>Nº Parcelas</Label>
            <Input type="number" min="1" max="24" value={parcelas}
              onChange={(e) => setParcelas(Math.max(1, Math.min(24, Number(e.target.value) || 1)))} />
          </div>
          <div className="space-y-2">
            <Label>NSU (opcional)</Label>
            <Input value={nsu} onChange={(e) => setNsu(e.target.value)} placeholder="Ex: 123456789" />
          </div>
          <div className="space-y-2">
            <Label>Cód. Autorização TEF</Label>
            <Input type="number" value={autenticacao} onChange={(e) => setAutenticacao(e.target.value)} />
          </div>
        </div>

        <div className="flex items-center gap-3">
          <Switch checked={aplicarAntecipacao} onCheckedChange={setAplicarAntecipacao} />
          <Label className="text-sm">Aplicar taxa de antecipação</Label>
        </div>

        <div className="space-y-2">
          <Label>Observação</Label>
          <Input value={observacao} onChange={(e) => setObservacao(e.target.value)} />
        </div>

        {/* Preview do cálculo */}
        {calculo && (
          <div className="p-4 rounded-lg border bg-muted/30 space-y-2">
            <h4 className="font-semibold flex items-center gap-2 text-sm">
              <Calculator className="h-4 w-4" />Cálculo Automático {calculando && <Loader2 className="h-3 w-3 animate-spin" />}
            </h4>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
              <PreviewItem label="Taxa %"      value={`${calculo.taxaPercentual.toFixed(2)}%`} />
              <PreviewItem label="Valor Taxa"  value={fmtCur(calculo.valorTaxaPercentual)} color="text-red-600" />
              <PreviewItem label="Adicional"   value={fmtCur(calculo.valorAdicionalFixo)} color="text-red-600" />
              <PreviewItem label="Antecipação" value={fmtCur(calculo.valorAntecipacao)} color="text-red-600" />
              <PreviewItem label="Líquido"     value={fmtCur(calculo.valorLiquido)} color="text-green-600" bold />
              <PreviewItem label="Prazo"       value={`${calculo.prazoDias} dias`} />
              <PreviewItem label="Previsão"    value={fmtDate(calculo.dtPrevisao)} />
              <PreviewItem label="Por parcela" value={fmtCur(calculo.valorLiquido / parcelas)} />
            </div>
          </div>
        )}

        <Button onClick={handleSalvar} disabled={salvando || !cartaoId} className="w-full md:w-auto">
          {salvando
            ? <><Loader2 className="h-4 w-4 animate-spin mr-2" />Salvando...</>
            : <><Plus className="h-4 w-4 mr-2" />Lançar Movimento</>}
        </Button>
      </CardContent>
    </Card>
  );
}

// ════════════════════════════════════════════════════════════════════════════
//  Aba Conciliação
// ════════════════════════════════════════════════════════════════════════════

function ConciliacaoTab({
  cartoes, contas,
}: {
  cartoes: { id: number; nome: string }[];
  contas: { id: number; descricao: string }[];
}) {
  const { toast } = useToast();
  const [pendentes, setPendentes] = useState<MovCartao[]>([]);
  const [selecionados, setSelecionados] = useState<Set<number>>(new Set());
  const [dtPrevisaoAte, setDtPrevisaoAte] = useState(daysAhead(7));
  const [cartaoId, setCartaoId] = useState<number | undefined>(undefined);
  const [contaCorrenteId, setContaCorrenteId] = useState<number | undefined>(undefined);
  const [dtRecebimento, setDtRecebimento] = useState(today());
  const [valorOverride, setValorOverride] = useState('');
  const [carregando, setCarregando] = useState(false);
  const [conciliando, setConciliando] = useState(false);

  const carregar = useCallback(async () => {
    setCarregando(true);
    try {
      const lista = await cartaoMovimentoService.pendentesConciliacao(dtPrevisaoAte, cartaoId);
      setPendentes(lista);
      setSelecionados(new Set());
    } catch (e) {
      toast({ title: 'Erro ao carregar', description: getApiErrorMessage(e), variant: 'destructive' });
    } finally {
      setCarregando(false);
    }
  }, [dtPrevisaoAte, cartaoId, toast]);

  useEffect(() => { carregar(); }, [carregar]);

  const toggleSelecao = (id: number) => {
    setSelecionados((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const selecionarTodos = () => {
    if (selecionados.size === pendentes.length) {
      setSelecionados(new Set());
    } else {
      setSelecionados(new Set(pendentes.map((p) => p.id)));
    }
  };

  const totalLiquidoSelecionado = useMemo(
    () => pendentes.filter((p) => selecionados.has(p.id)).reduce((s, m) => s + m.valorLiquido, 0),
    [pendentes, selecionados],
  );

  const handleConciliar = useCallback(async () => {
    if (selecionados.size === 0) { toast({ title: 'Selecione ao menos um movimento', variant: 'destructive' }); return; }
    if (!contaCorrenteId) { toast({ title: 'Selecione conta corrente', variant: 'destructive' }); return; }

    setConciliando(true);
    try {
      const r = await cartaoMovimentoService.conciliar({
        movCartoesIds: Array.from(selecionados),
        contaCorrenteRecebimentoId: contaCorrenteId,
        dtRecebimento,
        valorRecebidoTotalOverride: valorOverride ? Number(valorOverride) : undefined,
      });
      toast({ title: r.mensagem });
      await carregar();
    } catch (e) {
      toast({ title: 'Erro na conciliação', description: getApiErrorMessage(e), variant: 'destructive' });
    } finally {
      setConciliando(false);
    }
  }, [selecionados, contaCorrenteId, dtRecebimento, valorOverride, carregar, toast]);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Conciliar Recebimentos de Cartão</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div className="space-y-1">
            <Label className="text-xs">Previsão até</Label>
            <Input type="date" value={dtPrevisaoAte} onChange={(e) => setDtPrevisaoAte(e.target.value)} className="h-9" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Cartão</Label>
            <Select value={cartaoId?.toString() ?? 'todos'}
              onValueChange={(v) => setCartaoId(v === 'todos' ? undefined : Number(v))}>
              <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="todos">Todos</SelectItem>
                {cartoes.map((c) => <SelectItem key={c.id} value={c.id.toString()}>{c.nome}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-end">
            <Button variant="outline" size="sm" onClick={carregar} disabled={carregando} className="w-full">
              <RefreshCw className={`h-3 w-3 mr-1 ${carregando ? 'animate-spin' : ''}`} />Atualizar
            </Button>
          </div>
        </div>

        {/* Tabela de seleção */}
        <div className="border rounded-lg overflow-x-auto max-h-96 overflow-y-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-10">
                  <Checkbox
                    checked={pendentes.length > 0 && selecionados.size === pendentes.length}
                    onCheckedChange={selecionarTodos}
                  />
                </TableHead>
                <TableHead>Cartão</TableHead>
                <TableHead>Dt Mov.</TableHead>
                <TableHead>Dt Prev.</TableHead>
                <TableHead className="text-right">Bruto</TableHead>
                <TableHead className="text-right">Taxa</TableHead>
                <TableHead className="text-right">Líquido</TableHead>
                <TableHead>NSU</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {pendentes.length === 0 ? (
                <TableRow><TableCell colSpan={8} className="text-center text-muted-foreground py-6">
                  Nenhum movimento pendente.
                </TableCell></TableRow>
              ) : pendentes.map((m) => (
                <TableRow key={m.id} className={selecionados.has(m.id) ? 'bg-muted/30' : ''}>
                  <TableCell>
                    <Checkbox checked={selecionados.has(m.id)} onCheckedChange={() => toggleSelecao(m.id)} />
                  </TableCell>
                  <TableCell className="font-medium">{m.nomeCartao}</TableCell>
                  <TableCell>{fmtDate(m.dtLancamento)}</TableCell>
                  <TableCell>{fmtDate(m.dtPrevisaoCartao)}</TableCell>
                  <TableCell className="text-right tabular-nums">{fmtCur(m.valorOperacao)}</TableCell>
                  <TableCell className="text-right tabular-nums text-red-600">
                    {fmtCur(m.valorTaxa + m.valorAdicional + m.valorAntecipacao)}
                  </TableCell>
                  <TableCell className="text-right tabular-nums font-semibold">{fmtCur(m.valorLiquido)}</TableCell>
                  <TableCell className="font-mono text-xs">{m.nsu ?? '—'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>

        {/* Painel de conciliação */}
        {selecionados.size > 0 && (
          <div className="p-4 rounded-lg border bg-blue-50 dark:bg-blue-950/30 space-y-3">
            <h4 className="font-semibold flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4 text-blue-600" />
              Confirmar Conciliação ({selecionados.size} movimento{selecionados.size !== 1 ? 's' : ''})
            </h4>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div className="space-y-1">
                <Label className="text-xs">Conta de Crédito</Label>
                <Select value={contaCorrenteId?.toString() ?? ''}
                  onValueChange={(v) => setContaCorrenteId(Number(v))}>
                  <SelectTrigger className="h-9"><SelectValue placeholder="Selecione" /></SelectTrigger>
                  <SelectContent>
                    {contas.map((c) => <SelectItem key={c.id} value={c.id.toString()}>{c.descricao}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Data do Recebimento</Label>
                <Input type="date" value={dtRecebimento} onChange={(e) => setDtRecebimento(e.target.value)} className="h-9" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Valor Recebido (override opcional)</Label>
                <Input type="number" step="0.01" placeholder={fmtCur(totalLiquidoSelecionado)}
                  value={valorOverride} onChange={(e) => setValorOverride(e.target.value)} className="h-9" />
              </div>
            </div>

            <div className="text-sm space-y-1">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Total líquido esperado:</span>
                <span className="font-semibold tabular-nums">{fmtCur(totalLiquidoSelecionado)}</span>
              </div>
              {valorOverride && Number(valorOverride) !== totalLiquidoSelecionado && (
                <div className="flex justify-between text-orange-600">
                  <span>Diferença:</span>
                  <span className="font-semibold tabular-nums">
                    {fmtCur(Number(valorOverride) - totalLiquidoSelecionado)}
                  </span>
                </div>
              )}
            </div>

            <Button onClick={handleConciliar} disabled={conciliando || !contaCorrenteId} className="w-full md:w-auto">
              {conciliando
                ? <><Loader2 className="h-4 w-4 animate-spin mr-2" />Conciliando...</>
                : <><CheckCircle2 className="h-4 w-4 mr-2" />Confirmar Conciliação</>}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ── Subcomponentes ───────────────────────────────────────────────────────────

function KpiCard({ label, value, color = 'text-foreground' }: { label: string; value: number; color?: string }) {
  return (
    <div className="p-3 rounded-lg border">
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className={`text-lg font-bold tabular-nums ${color}`}>
        {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value)}
      </p>
    </div>
  );
}

function PreviewItem({
  label, value, color, bold,
}: { label: string; value: string; color?: string; bold?: boolean }) {
  return (
    <div>
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className={`tabular-nums ${color ?? ''} ${bold ? 'font-bold' : ''}`}>{value}</p>
    </div>
  );
}
